import React, { createContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { Account } from 'appwrite';
import client, { IMPERSONATION_STORAGE_KEY, applyImpersonationTarget, getStoredImpersonationTarget } from './app-user';

interface UserSession {
  id: string;
  email: string;
  name: string;
  labels: string[];
  role?: string;
  impersonatorUserId?: string;
}

interface UserState {
  session: UserSession | null;
}

interface AuthContextType {
  user: UserState;
  setUser: React.Dispatch<React.SetStateAction<UserState>>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  impersonatedUserId: string | null;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: { session: null },
  setUser: () => {},
  login: async () => {},
  logout: async () => {},
  checkSession: async () => {},
  impersonatedUserId: null,
  startImpersonation: async () => {},
  stopImpersonation: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<UserState>({ session: null });
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(() => getStoredImpersonationTarget());
  const account = useMemo(() => new Account(client), []);

  const fetchUserDetails = useCallback(async (): Promise<UserSession | null> => {
    try {
      const result = await account.get();

      if (result) {
        return {
          id: result.$id,
          email: result.email,
          name: result.name,
          labels: result.labels,
          impersonatorUserId: (result as any).impersonatorUserId,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return null;
    }
  }, [account]);

  const checkSession = useCallback(async () => {
    const userDetails = await fetchUserDetails();
    if (userDetails) {
      setUser({ session: userDetails });
    } else {
      setUser({ session: null });
    }
  }, [fetchUserDetails]);

  const login = async (email: string, password: string) => {
    try {
      applyImpersonationTarget(null);
      window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      setImpersonatedUserId(null);
      await account.createEmailPasswordSession(email, password);
      await checkSession();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('logout called...');
      applyImpersonationTarget(null);
      window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      setImpersonatedUserId(null);
      setUser({ session: null });
      await account.deleteSession('current');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };


  const startImpersonation = async (userId: string) => {
    applyImpersonationTarget(userId);
    window.localStorage.setItem(IMPERSONATION_STORAGE_KEY, userId);
    setImpersonatedUserId(userId);
    await checkSession();
  };

  const stopImpersonation = async () => {
    applyImpersonationTarget(null);
    window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    setImpersonatedUserId(null);
    await checkSession();
  };

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, checkSession, impersonatedUserId, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};
